import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { map, of } from 'rxjs';
import { AuthenticationService } from '../../../authentication/authentication.service';
import { MceApiService, ProjectDto, UserDto } from '../../../core/mceapi.client.generated';
import { MceSaveCancelComponent } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelEvent } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelConfig } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { FormDirective } from '../../directives/form.directive';
import { match } from '../../functions/functional';

interface FormModel { toProjectUid: string; }


@Component({
  selector: 'app-move-cable-dialog',
  imports: [FormDirective, FormsModule, ReactiveFormsModule, MatInputModule, MatProgressBarModule,
    MatDialogModule, MatSelectModule, MceSaveCancelComponent],
  templateUrl: './move-cable-dialog.component.html',

})

export class MoveCableDialogComponent {

  private data = inject<FormModel>(MAT_DIALOG_DATA);
  fromProjectUid = signal(this.data.toProjectUid);

  private dialogRef = inject(MatDialogRef<MoveCableDialogComponent>);
  private authenticationSerivcie = inject(AuthenticationService);
  private mceApiService = inject(MceApiService);

  protected formValue = { toProjectUid: ''};

  protected formValid = signal<boolean>(false);
  protected formDirty = signal<boolean>(false);
  formValueChange = signal<any>(true);

  protected toProjectsResource = rxResource({
    params: () => ({ fromUid: this.fromProjectUid() }),
    stream: ({ params }) =>
      match(
        () => of([]),
        (user: UserDto) => {
          const userUid = user.uid;
          if (!userUid) return of([]);

          return this.mceApiService.v1ProjectsUser(userUid).pipe(
            map(toProjects => toProjects.filter(toProject => toProject.uid !== params.fromUid)),
            map(toProjects => toProjects.sort((a, b) =>
              (a.projectNumber ?? '').localeCompare(b.projectNumber ?? '')))
          );
        })
        (this.authenticationSerivcie.currentUser())
  });
  toProjects = computed<ProjectDto[]>(() => this.toProjectsResource.value() ?? []);

  protected readonly viewModel = computed(() => {
    return {
      formValue: this.formValue,
      formValid: this.formValid(),
      formDirty: this.formDirty(),

      noToProjects: this.toProjects().length === 0,
      someToProjects: this.toProjects().length > 0,

      toProjects: this.toProjects().map((x: ProjectDto) => ({
        value: x.uid,
        label: `${x.projectNumber}: ${ x.projectName }`})),

      showLoading: this.toProjectsResource.isLoading(),

      enableOk: this.formValue.toProjectUid !== ''

    };
  });

  protected get vm() { return this.viewModel(); }

  protected readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    ok: { show: true, enable: this.vm.enableOk },
    cancel: { show: true, enable: true }
  }));


  onOkClick(): void {
    this.dialogRef.close(this.formValue);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'ok') {
      this.onOkClick();
      return;
    }

    if (event.type === 'cancel') {
      this.onCancelClick();
    }
  }

}
